import React, { useEffect, useRef, useState } from 'react';

// Props

type Props = {
  section: any;
};

// All Jobs

type JobMetadata = {
  id: string;
  name: string;
  value: string | null;
  value_type: string;
};

type JobDataCompliance = {
  type: string;
  requires_consent: boolean;
  requires_processing_consent: boolean;
  requires_retention_consent: boolean;
  retention_period: string | null;
  demographic_data_consent_applies: boolean;
};

type GreenhouseJob = {
  id: string;
  internal_job_id?: number;
  requisition_id?: string;
  title: string;
  company_name?: string;
  absolute_url: string;
  location?: { name?: string };
  metadata?: JobMetadata[];
  data_compliance?: JobDataCompliance[];
  first_published?: string;
  updated_at?: string;
  region?: string;
};

// Single Job

type GreenhouseDepartment = {
  id: number;
  name: string;
  child_ids: number[];
  parent_id: number | null;
};

type HTMLString = string;

type GreenhouseOffice = unknown;

type SingleGreenhouseJob = {
  absolute_url: string;
  data_compliance: JobDataCompliance[];
  internal_job_id: number | null;
  location: { name: string };
  metadata: JobMetadata[];
  id: number;
  updated_at: string;
  requisition_id: string | null;
  title: string;
  company_name: string;
  first_published: string;
  content: HTMLString;
  departments: GreenhouseDepartment[];
  offices: GreenhouseOffice[];
};

const GreenhouseAPIComponent: React.FC<Props> = ({ section }) => {
  const filters = useRef<HTMLDivElement | null>(null);

  // All Jobs
  const [regions] = useState<{ code: string; title: string }[]>([
    { code: 'telixus', title: 'US' },
    { code: 'telixapac', title: 'APAC' },
    { code: 'telixemea', title: 'EMEA' },
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegion, setFilterRegion] = useState<string>(''); // Select
  const [filterRegions, setFilterRegions] = useState<string[]>([]); // Buttons
  const [filterRole, setFilterRole] = useState<string>(''); // Select
  const [filterRoles, setFilterRoles] = useState<string[]>([]); // Buttons
  const [jobs, setJobs] = useState<GreenhouseJob[]>([]);
  const [loadingAllJobs, setLoadingAllJobs] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const jobsByRegion = filterRegion
    ? jobs.filter((job) => job.region === filterRegion)
    : jobs.filter(
        (job) =>
          filterRegions.length === 0 || filterRegions.includes(job.region || '')
      );

  const roles = Array.from(
    new Set(jobsByRegion.map((job) => job.title))
  ).sort();

  // Single Job
  const [singleJob, setSingleJob] = useState<SingleGreenhouseJob | null>(null);

  const [singleJobID, setSingleJobID] = useState<string | null>(null);
  const [singleJobRegion, setSingleJobRegion] = useState<string | null>(null);
  const [loadingSingleJob, setLoadingSingleJob] = useState<boolean | null>(
    null
  );

  // Misc.
  const clearSingleJob = (): void => {
    setSingleJob(null);
    setSingleJobID(null);
    setSingleJobRegion(null);

    const url = new URL(window.location.href);
    if (!filterRegion) {
      url.searchParams.delete('region');
    }
    url.searchParams.delete('gh_jid');
    window.history.replaceState({}, '', url.toString());
  };

  // Filter Select
  const handleChangeRegion = (
    e: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    clearSingleJob();
    setFilterRole('');
    setFilterRegion(e.target.value);

    const url = new URL(window.location.href);
    url.searchParams.delete('region');
    window.history.replaceState({}, '', url.toString());

    if (e.target.value) {
      const url = new URL(window.location.href);
      url.searchParams.set('region', e.target.value);
      window.history.replaceState({}, '', url.toString());
    }
  };

  // Filter Buttons
  const handleRegionClick = (code: string) => {
    setFilterRegions((prev) =>
      prev.includes(code) ? prev.filter((r) => r !== code) : [...prev, code]
    );
    setFilterRoles([]);
    clearSingleJob();
  };
  const handleRoleClick = (code: string) => {
    setFilterRoles((prev) =>
      prev.includes(code) ? prev.filter((r) => r !== code) : [...prev, code]
    );
    clearSingleJob();
  };

  // Check URL Params on Load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Load Filtered Jobs
    const regionParam = params.get('region');
    // if (regionParam) setFilterRegion(regionParam); // Select (Single value only)
    if (regionParam) {
      setFilterRegions(regionParam.split(','));
    }

    // Load Single Job
    const jobIdParam = params.get('gh_jid');
    if (jobIdParam) {
      setSingleJobID(jobIdParam);
      setSingleJobRegion(regionParam);
    }
  }, []);

  // Update URL Params
  useEffect(() => {
    const url = new URL(window.location.href);

    if (filterRegions.length === 0) {
      url.searchParams.delete('region');
    } else {
      const other = new URLSearchParams(url.search);
      other.delete('region');
      const qs = other.toString();
      const prefix = qs ? `?${qs}&` : '?';
      url.search = `${prefix}region=${filterRegions.join(',')}`;
    }

    window.history.replaceState({}, '', url.toString());
  }, [filterRegions]);

  // Get jobs from all regions
  useEffect(() => {
    const controller = new AbortController();

    setLoadingAllJobs(true);
    setError(null);

    const getJobs = (region: string): any =>
      fetch(`https://boards-api.greenhouse.io/v1/boards/${region}/jobs`, {
        signal: controller.signal,
      })
        .then((res) => {
          if (!res.ok) throw new Error(`${region}: HTTP ${res.status}`);
          return res.json() as Promise<{ jobs?: GreenhouseJob[] }>;
        })
        .then((data) =>
          (data.jobs ?? []).map((j) => ({ ...j, region } as GreenhouseJob))
        );

    Promise.allSettled(regions.map((r) => getJobs(r.code)))
      .then((results) => {
        const fulfilled = results.filter(
          (r): r is PromiseFulfilledResult<GreenhouseJob[]> =>
            r.status === 'fulfilled'
        );
        const rejected = results.filter(
          (r): r is PromiseRejectedResult => r.status === 'rejected'
        );

        const combined = fulfilled.flatMap((r) => r.value);

        // Sort newest updated/published first
        combined.sort((a, b) => {
          const da = a.updated_at ?? a.first_published ?? '';
          const db = b.updated_at ?? b.first_published ?? '';
          return db.localeCompare(da);
        });

        setJobs(combined);

        if (rejected.length) {
          const messages = rejected.map((r) =>
            r.reason instanceof Error ? r.reason.message : String(r.reason)
          );
          setError(
            fulfilled.length
              ? `Some regions failed: ${messages.join(' | ')}`
              : `Failed to load jobs: ${messages.join(' | ')}`
          );
        }
      })
      .catch((err: any) => {
        if (err?.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Unknown error');
      })
      .finally(() => {
        setLoadingAllJobs(false);
      });

    return (): void => controller.abort();
  }, []);

  // Get single job details
  useEffect(() => {
    if (!singleJobID || !singleJobRegion) return;

    const controller = new AbortController();

    setLoadingSingleJob(true);
    setError(null);

    const url = `https://boards-api.greenhouse.io/v1/boards/${singleJobRegion}/jobs/${singleJobID}`;

    fetch(url, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<SingleGreenhouseJob>;
      })
      .then((data) => {
        setSingleJob(data);
      })
      .catch((err: any) => {
        if (err?.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Unknown error');
      })
      .finally(() => {
        setLoadingSingleJob(false);

        setTimeout(() => {
          const offset = 90;
          const elementPosition =
            section.getBoundingClientRect().top + window.pageYOffset;

          window.scrollTo({
            top: elementPosition - offset,
            behavior: 'smooth',
          });
        }, 300);
      });

    return (): void => controller.abort();
  }, [singleJobID, singleJobRegion]);

  // Greenhouse
  useEffect(() => {
    if (!singleJobRegion) return;

    const container = document.getElementById('grnhse_app');

    if (!container) return;

    container.innerHTML = '';

    const startEmbed = () => {
      const GH = (window as any).Grnhse;
      if (GH?.Iframe?.load) {
        GH.Iframe.load(singleJobID || undefined);
      } else {
        setTimeout(startEmbed, 25);
      }
    };

    const selector = `script[src*="https://boards.greenhouse.io/embed/job_board/js?for=${singleJobRegion}"]`;
    const existing = document.querySelector<HTMLScriptElement>(selector);

    if (existing) {
      startEmbed();
      return;
    }

    const script = document.createElement('script');

    script.src = `https://boards.greenhouse.io/embed/job_board/js?for=${encodeURIComponent(
      singleJobRegion
    )}&t=${Date.now()}`;
    script.async = true;
    script.dataset.ghEmbed = 'true';

    script.onload = startEmbed;
    script.onerror = () => {
      console.error('Failed to load Greenhouse embed script');
    };

    (document.head || document.body).appendChild(script);

    return () => {
      container.innerHTML = '';
    };
  }, [singleJobRegion, singleJobID]);

  // Greenhouse - Remove iFrame
  useEffect(() => {
    if (singleJob) return;

    const container = document.getElementById('grnhse_app');

    if (container) {
      container.innerHTML = '';
    }
  }, [singleJob]);

  // Accordions

  const classes = {
    accordionOpen: 'job-listings-greenhouse-api__filter-accordion--open',
  };

  const openAccordion = (
    accordion: HTMLElement,
    accordionContent: HTMLElement
  ): void => {
    accordion.classList.add(classes.accordionOpen);
    accordionContent.style.maxHeight = accordionContent.scrollHeight + 'px';
  };

  const closeAccordion = (
    accordion: HTMLElement,
    accordionContent: HTMLElement
  ): void => {
    accordion.classList.remove(classes.accordionOpen);
    accordionContent.style.maxHeight = null as any;
  };

  useEffect(() => {
    const accordions =
      filters.current?.querySelectorAll<HTMLElement>('[data-accordion]');

    accordions?.forEach((accordion) => {
      const toggle = accordion.querySelector<HTMLElement>(
        '[data-accordion-toggle]'
      );
      const content = accordion.querySelector<HTMLElement>(
        '[data-accordion-content]'
      );

      if (toggle && content) {
        toggle.addEventListener('click', () => {
          if (content.style.maxHeight) {
            closeAccordion(accordion, content);
          } else {
            accordions.forEach((otherAccordion) => {
              const otherContent: any =
                otherAccordion.querySelector<HTMLElement>(
                  '[data-accordion-content]'
                );
              closeAccordion(otherAccordion, otherContent);
            });
            openAccordion(accordion, content);
          }
        });
      }
    });
  }, [filters]);

  useEffect(() => {
    const accordions =
      filters.current?.querySelectorAll<HTMLElement>('[data-accordion]');

    accordions?.forEach((accordion) => {
      const content = accordion.querySelector<HTMLElement>(
        '[data-accordion-content]'
      );

      if (content) {
        if (accordion.getAttribute('data-accordion-open-on-load') == 'true') {
          openAccordion(accordion, content);
        }
      }
    });
  }, [filterRegions]);

  if (error) return <p>Error: {error}</p>;

  return (
    <>
      {/* Filters */}
      <div ref={filters} className="job-listings-greenhouse-api__filters">
        <div>
          <h3>Filters</h3>
          {/* Search */}
          <div
            className={`job-listings-greenhouse-api__search ${
              searchTerm != ''
                ? `job-listings-greenhouse-api__search--active`
                : ``
            }`}
          >
            <label htmlFor="search">Search:</label>
            <input
              type="text"
              id="search"
              name="search"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && searchTerm != '' ? (
              <button
                onClick={() => {
                  setSearchTerm('');
                }}
              >
                <i className="fa-solid fa-circle-xmark"></i>
              </button>
            ) : (
              <span>
                <i className="far fa-search"></i>
              </span>
            )}
          </div>

          {/* Region */}
          <div className="job-listings-greenhouse-api__filter">
            <label htmlFor="region-select">Select Region: </label>
            <select
              id="region-select"
              value={filterRegion}
              onChange={handleChangeRegion}
            >
              <option value="">Filter by Region</option>
              {regions.map((region) => (
                <option key={region.code} value={region.code}>
                  {region.title}
                </option>
              ))}
            </select>
            <div>
              <i className="fa-light fa-chevron-down"></i>
            </div>
          </div>

          <div
            data-accordion
            data-accordion-open-on-load={filterRegions.length ? true : false}
            className="job-listings-greenhouse-api__filter-accordion job-listings-greenhouse-api__filter-accordion--regions"
          >
            <button data-accordion-toggle className="accordion__toggle">
              <span className="h5">Region</span>
            </button>
            <div data-accordion-content className="accordion__content">
              {regions.map((region) => (
                <button
                  key={region.code}
                  value={region.code}
                  onClick={() => handleRegionClick(region.code)}
                  className={
                    filterRegions.includes(region.code) ? 'active' : ''
                  }
                >
                  {region.title}
                </button>
              ))}
            </div>
          </div>

          {/* Role Filter */}
          <div className="job-listings-greenhouse-api__filter">
            <label htmlFor="role-select">Select Role: </label>
            <select
              id="role-select"
              value={filterRole}
              onChange={(e) => {
                clearSingleJob();
                setFilterRole(e.target.value);
              }}
            >
              <option value="">
                All {regions.find((r) => r.code === filterRegion)?.title || ''}{' '}
                roles
              </option>
              {roles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
            <div>
              <i className="fa-light fa-chevron-down"></i>
            </div>
          </div>

          <div
            data-accordion
            className="job-listings-greenhouse-api__filter-accordion job-listings-greenhouse-api__filter-accordion--roles"
          >
            <button data-accordion-toggle className="accordion__toggle">
              <span className="h5">Role</span>
            </button>
            <div data-accordion-content className="accordion__content">
              {roles.map((role) => (
                <button
                  key={role}
                  value={role}
                  onClick={() => handleRoleClick(role)}
                  className={filterRoles.includes(role) ? 'active' : ''}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>

          {filterRegions.length || filterRoles.length ? (
            <button
              className="filters__clear"
              onClick={() => {
                setFilterRegions([]);
                setFilterRoles([]);
                clearSingleJob();
              }}
            >
              Clear Filters
            </button>
          ) : null}
        </div>
      </div>

      {singleJob || loadingSingleJob ? (
        <>
          <div className="job-listings-greenhouse-api__back-button">
            <button
              onClick={() => {
                clearSingleJob();
              }}
            >
              <i className="far fa-long-arrow-left"></i>Back
            </button>
          </div>

          {loadingSingleJob ? (
            <div className="job-listings-greenhouse-api__single-job-loading">
              <p>Loading...</p>
            </div>
          ) : (
            singleJob && (
              <div className="job-listings-greenhouse-api__single-job">
                {/* Header */}
                <div className="single-job__header">
                  <p className="single-job__tags">
                    <span>
                      {regions.find((r) => r.code === singleJobRegion)?.title ??
                        singleJobRegion}
                    </span>
                    <span>{singleJob.location.name}</span>
                  </p>
                  <h3>{singleJob.title}</h3>
                  <p>
                    <span className="h5">Updated: </span>
                    {(() => {
                      const dateObj = new Date(singleJob.updated_at);
                      const formattedDate = dateObj.toLocaleDateString(
                        'en-GB',
                        {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        }
                      );
                      return <>{formattedDate}</>;
                    })()}
                  </p>
                </div>

                {/* Main Content */}
                <div className="single-job__content">
                  {(() => {
                    const htmlDecode = (input: string): string => {
                      const e = document.createElement('textarea');
                      e.innerHTML = input;
                      return e.value;
                    };
                    let decoded = htmlDecode(singleJob.content);

                    // Remove empty or whitespace-only paragraphs (e.g. <p>&nbsp;</p>, <p> </p>, <p></p>)
                    decoded = decoded.replace(
                      /<p>\s*(?:&nbsp;|\u00A0)?\s*<\/p>/gi,
                      ''
                    );

                    return (
                      <div dangerouslySetInnerHTML={{ __html: decoded }} />
                    );
                  })()}
                </div>
              </div>
            )
          )}
        </>
      ) : loadingAllJobs ? (
        <div className="job-listings-greenhouse-api__all-jobs-loading">
          <p>Loading...</p>
        </div>
      ) : (
        <>
          {/* All Jobs */}
          <ul className="job-listings-greenhouse-api__all-jobs">
            {jobs
              // Search
              .filter(
                (job) =>
                  !searchTerm ||
                  job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  job.location?.name
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase())
              )
              // Selects
              .filter(
                (job) =>
                  (!filterRegion || job.region === filterRegion) &&
                  (!filterRole || job.title === filterRole)
              )
              // Buttons
              .filter(
                (job) =>
                  (filterRegions.length === 0 ||
                    filterRegions.includes(job.region ?? '')) &&
                  (filterRoles.length === 0 ||
                    filterRoles.includes(job.title ?? ''))
              )
              .map((job) => (
                <li key={job.id} className="job-listings-greenhouse-api__job">
                  <div>
                    {job.region && (
                      <span>
                        <span>
                          {regions.find((r) => r.code === job.region)?.title ??
                            job.region}
                        </span>
                      </span>
                    )}
                    {job.location?.name && (
                      <span>
                        <span>{job.location.name}</span>
                      </span>
                    )}
                  </div>
                  <h4>{job.title}</h4>
                  {job.updated_at && (
                    <p>
                      <span className="h5">Updated: </span>
                      {(() => {
                        const dateObj = new Date(job.updated_at);
                        const formattedDate = dateObj.toLocaleDateString(
                          'en-GB',
                          {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }
                        );
                        return <>{formattedDate}</>;
                      })()}
                    </p>
                  )}
                  <button
                    onClick={() => {
                      setSingleJobID(job.id);
                      setSingleJobRegion(job.region || '');

                      const url = new URL(window.location.href);
                      url.searchParams.set('region', String(job.region || ''));
                      url.searchParams.set('gh_jid', String(job.id));
                      window.history.replaceState({}, '', url.toString());
                    }}
                  >
                    Apply Now
                  </button>
                </li>
              ))}
          </ul>
        </>
      )}

      <div
        id="grnhse_app"
        className="job-listings-greenhouse-api__single-job-form"
      ></div>
    </>
  );
};

export default GreenhouseAPIComponent;
